import { useEffect, useCallback, useRef } from "react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { SubHeading } from "../ui/sub-heading";
import { Loader } from "../ui/loader";
import { API_ENDPOINTS } from "@/config/api";
import { apiRequest } from "@/utils/api";
import { ErrorDisplay } from "../ui/error";
import { Assessment } from "@/types";

interface JobDetailsStepProps {
  assessment: Assessment;
  onInputChange: (field: keyof Assessment, value: string) => void;
  isGenerating: boolean;
  setIsGenerating: (isGenerating: boolean) => void;
  selectedFile: File | null;
  inputText: string;
  setInputText: (text: string) => void;
  setError: (error: string) => void;
  error: string;
}

export function JobDetailsStep({
  assessment: jobSpec,
  onInputChange,
  isGenerating,
  setIsGenerating,
  selectedFile,
  inputText,
  setInputText,
  setError,
  error,
}: JobDetailsStepProps) {
  const isInitialized = useRef(false);
  const handleJobSpecExtraction = useCallback(async () => {
    if (!selectedFile && !inputText) return;

    setIsGenerating(true);
    setError("");

    try {
      let data: Assessment;

      // Handle file upload
      if (selectedFile) {
        if (selectedFile.type !== "application/pdf") {
          setError("Please upload a PDF file");
          return;
        }

        const formData = new FormData();
        formData.append("file", selectedFile);
        data = await apiRequest<Assessment>(
          API_ENDPOINTS.assessments.extractPdf,
          {
            method: "POST",
            body: formData,
          }
        );
        setInputText(data.raw_text || "");
      }
      // Handle text input
      else {
        if (!inputText.trim()) {
          setError("Please paste the job specification text");
          return;
        }

        data = await apiRequest<Assessment>(
          API_ENDPOINTS.assessments.extractText,
          {
            method: "POST",
            body: { text: inputText },
          }
        );
      }

      onInputChange("raw_text", data.raw_text || "");
      onInputChange("title", data.title || "");
      onInputChange("language", data.language || "");
      onInputChange("location", data.location || "");
      onInputChange("importance", data.importance || "");
      onInputChange("skills", data.skills || "");
      onInputChange("ideal_candidate", data.ideal_candidate || "");
    } catch (err: any) {
      setError(err.message);
    } finally {
      setIsGenerating(false);
    }
  }, [
    selectedFile,
    inputText,
    setIsGenerating,
    setError,
    setInputText,
    onInputChange,
  ]);

  useEffect(() => {
    if (isInitialized.current) return;
    handleJobSpecExtraction();
    isInitialized.current = true;
  }, [handleJobSpecExtraction]);

  if (isGenerating) {
    return (
      <div className="flex justify-center py-8">
        <Loader title="Extracting job details" />
      </div>
    );
  }

  return (
    <>
      <SubHeading>Job details</SubHeading>
      <div className="space-y-4">
        <div className="space-y-2">
          <label className="text-sm font-medium">Job title</label>
          <Input
            value={jobSpec.title}
            onChange={(e) => onInputChange("title", e.target.value)}
            placeholder="Enter the job title"
          />
        </div>
        <div className="space-y-2">
          <label className="text-sm font-medium">Primary language</label>
          <Input
            value={jobSpec.language}
            onChange={(e) => onInputChange("language", e.target.value)}
            placeholder="e.g., English, Spanish"
          />
        </div>
        <div className="space-y-2">
          <label className="text-sm font-medium">Location</label>
          <Input
            value={jobSpec.location}
            onChange={(e) => onInputChange("location", e.target.value)}
            placeholder="Enter the job location or specify if remote"
          />
        </div>
        <div className="space-y-2">
          <label className="text-sm font-medium">Position importance</label>
          <Textarea
            value={jobSpec.importance}
            onChange={(e) => onInputChange("importance", e.target.value)}
            placeholder="Explain why this position is important"
          />
        </div>
        <div className="space-y-2">
          <label className="text-sm font-medium">
            Key skills & competencies
          </label>
          <Textarea
            value={jobSpec.skills}
            onChange={(e) => onInputChange("skills", e.target.value)}
            placeholder="List the key skills and competencies required for this position"
          />
        </div>
        <div className="space-y-2">
          <label className="text-sm font-medium">Ideal candidate profile</label>
          <Textarea
            value={jobSpec.ideal_candidate}
            onChange={(e) => onInputChange("ideal_candidate", e.target.value)}
            placeholder="Describe the type of person that would best suit this position"
          />
        </div>
      </div>
      {error && <ErrorDisplay message={error} />}
    </>
  );
}
